import { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, Upload, Mic, StopCircle, Check, X } from "lucide-react";
import api from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { toast, Toaster } from "sonner";

const STEPS = [
  { key: "perfil", label: "Perfil", n: "01" },
  { key: "historia", label: "Tu trayectoria", n: "02" },
  { key: "conversacion", label: "Conversación", n: "03" },
  { key: "fotos", label: "Fotos", n: "04" },
  { key: "produccion", label: "Producción", n: "05" },
];

const PHOTO_CATEGORIES = [
  { key: "momentos_importantes", title: "Momentos importantes", desc: "Sube hasta 3 fotos que representen momentos importantes de tu vida o trayectoria. Pueden ser personales, profesionales o de algún momento que haya marcado tu camino.", max: 3 },
  { key: "actualidad", title: "Foto actual", desc: "Fotografías profesionales recientes que podamos utilizar para presentar y promocionar tu episodio." },
];

const emptyForm = () => ({
  full_name: "", presentation_name: "", profession: "", company: "", city: "",
  country_origin: "", email: "", phone: "", instagram: "", linkedin: "", website: "", one_line: "",
  trajectory_expertise: "", trajectory_pivot: "", trajectory_representative: "",
  trajectory_current_role: "", trajectory_business_size: "", trajectory_controversial: "",
  topics: ["", "", ""], untold_story: "", industry_misconception: "", desired_question: "",
  topics_to_avoid: "", numeric_example: "", previous_media: false, previous_media_urls: [""],
  pronunciation_text: "", projects_to_know: "", additional_links: [""], additional_info: "", consent: false,
});

export default function Invitado() {
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem("modopro_invitado");
      return saved ? JSON.parse(saved) : emptyForm();
    } catch { return emptyForm(); }
  });
  const [submissionId, setSubmissionId] = useState(null);
  const [uploads, setUploads] = useState({}); // { category: [{id, filename, url}] }
  const [audioBlob, setAudioBlob] = useState(null);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    localStorage.setItem("modopro_invitado", JSON.stringify(form));
  }, [form]);

  useEffect(() => {
    document.title = "MODO PRO — Invitado";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!started) {
    return (
      <main data-testid="invitado-welcome" className="min-h-screen bg-mp-black">
        <div className="relative min-h-screen">
          <img src={ASSETS.studioEmpty} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
          <div className="relative z-10 mx-auto max-w-[900px] px-6 py-24 md:py-32">
            <img src={ASSETS.logo} alt="Modo Pro" width={80} height={80} />
            <div className="mp-eyebrow mt-10">Bienvenido a Modo Pro</div>
            <h1 className="mt-6 font-display font-bold text-white text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]">
              Queremos conocer tu historia <span className="text-mp-orange">antes de sentarnos a conversar</span>.
            </h1>
            <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl">
              La información que compartas nos ayudará a preparar tu entrevista, conocer mejor tu
              trayectoria y crear una presentación que represente tu historia de manera auténtica.
            </p>
            <p className="mt-4 text-white/80 text-base leading-relaxed max-w-2xl">
              No necesitas extenderte demasiado. Compártenos lo esencial y nosotros nos encargamos del resto.
            </p>
            <div className="mt-10 border-t border-white/10 pt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-mp-orange text-xs tracking-[0.24em] uppercase font-display">Tiempo estimado</div>
                <div className="mt-2 font-display text-white text-xl">8–12 minutos</div>
              </div>
              <div>
                <div className="text-mp-orange text-xs tracking-[0.24em] uppercase font-display">Prepara</div>
                <div className="mt-2 text-white/80 text-sm">
                  Ten a la mano algunas fotografías que representen momentos importantes de tu vida y trayectoria.
                </div>
              </div>
            </div>
            <button data-testid="invitado-comenzar" onClick={() => setStarted(true)} className="mp-btn-primary mt-14">
              Comenzar <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (done) {
    return (
      <main data-testid="invitado-done" className="min-h-screen bg-mp-black flex items-center justify-center px-6 py-24">
        <div className="max-w-2xl text-center">
          <img src={ASSETS.logo} alt="Modo Pro" width={90} height={90} className="mx-auto" />
          <div className="mp-eyebrow mt-10">¡Listo!</div>
          <h1 className="mt-4 font-display font-bold text-white text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]">
            Gracias por compartir tu <span className="text-mp-orange">historia</span>.
          </h1>
          <p className="mt-8 text-white/80 leading-relaxed">
            Nuestro equipo de producción de Modo Pro ha recibido tu información y comenzará a preparar tu participación.
          </p>
          <p className="mt-10 font-display text-white text-xl">
            Nos vemos frente a los <span className="text-mp-orange">micrófonos</span>.
          </p>
        </div>
      </main>
    );
  }

  const currentStep = STEPS[step].key;

  const uploadFile = async (subId, category, file, caption = "") => {
    const fd = new FormData();
    fd.append("category", category);
    fd.append("caption", caption);
    fd.append("file", file);
    const r = await api.post(`/invitado/${subId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Basic validation
      if (!form.consent) { toast.error("Debes aceptar la autorización."); setSubmitting(false); return; }
      const payload = {
        ...form,
        topics: form.topics.filter(Boolean),
        previous_media_urls: form.previous_media_urls.filter(Boolean),
        additional_links: form.additional_links.filter(Boolean),
      };
      const r = await api.post("/invitado/submit", payload);
      const subId = r.data.id;
      setSubmissionId(subId);
      // Upload photos
      const allPhotos = Object.entries(uploads).flatMap(([cat, files]) => files.map((f) => ({ cat, file: f._file, caption: f.caption || "" })));
      for (const p of allPhotos) {
        try { await uploadFile(subId, p.cat, p.file, p.caption); } catch (e) { console.warn(e); }
      }
      if (audioBlob) {
        try {
          const file = new File([audioBlob], "pronunciation.webm", { type: "audio/webm" });
          await uploadFile(subId, "pronunciation", file);
        } catch (e) { console.warn(e); }
      }
      localStorage.removeItem("modopro_invitado");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar. Intenta nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="invitado-form" className="min-h-screen bg-mp-black pt-28 pb-24">
      <Toaster theme="dark" />
      <div className="mx-auto max-w-[1000px] px-6 lg:px-12">
        {/* Progress */}
        <div className="grid grid-cols-5 gap-2 mb-14">
          {STEPS.map((s, i) => (
            <div key={s.key} className="border-t-2 pt-3" style={{ borderColor: i <= step ? "#FF6B1A" : "rgba(255,255,255,0.12)" }}>
              <div className={`font-display text-[10px] tracking-[0.24em] uppercase ${i <= step ? "text-mp-orange" : "text-mp-mute"}`}>{s.n}</div>
              <div className={`font-display text-xs md:text-sm mt-1 ${i <= step ? "text-white" : "text-mp-mute"}`}>{s.label}</div>
            </div>
          ))}
        </div>

        {currentStep === "perfil" && (
          <StepShell title={<>Cuéntanos <span className="text-mp-orange">quién eres</span>.</>} n="01">
            <Grid2>
              <Field label="Nombre completo *" testid="f-full_name"><input className="mp-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></Field>
              <Field label="¿Cómo prefieres que te presentemos? *" testid="f-presentation_name"><input className="mp-input" value={form.presentation_name} onChange={(e) => set("presentation_name", e.target.value)} /></Field>
              <Field label="Profesión / Cargo *" testid="f-profession"><input className="mp-input" value={form.profession} onChange={(e) => set("profession", e.target.value)} /></Field>
              <Field label="Empresa / Organización" testid="f-company"><input className="mp-input" value={form.company} onChange={(e) => set("company", e.target.value)} /></Field>
              <Field label="Ciudad donde resides *" testid="f-city"><input className="mp-input" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label="País de origen" testid="f-country_origin"><input className="mp-input" value={form.country_origin} onChange={(e) => set("country_origin", e.target.value)} /></Field>
              <Field label="Email *" testid="f-email"><input type="email" className="mp-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Teléfono *" testid="f-phone"><input className="mp-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
              <Field label="Instagram" testid="f-instagram"><input className="mp-input" placeholder="@usuario" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} /></Field>
              <Field label="LinkedIn" testid="f-linkedin"><input className="mp-input" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} /></Field>
              <Field label="Website" testid="f-website" span={2}><input className="mp-input" value={form.website} onChange={(e) => set("website", e.target.value)} /></Field>
            </Grid2>
            <Field label="Describe en una frase a qué te dedicas." testid="f-one_line">
              <textarea className="mp-input" rows={2} placeholder="Ej: Ayudo a parejas a cumplir su sueño de formar una familia mediante tratamientos avanzados de fertilidad." value={form.one_line} onChange={(e) => set("one_line", e.target.value)} />
            </Field>
          </StepShell>
        )}

        {currentStep === "historia" && (
          <StepShell title={<>Tu <span className="text-mp-orange">trayectoria</span>.</>} n="02">
            <Field label="Cuéntanos brevemente quién eres y qué experiencia te llevó a convertirte en experto(a) en el tema del que hablaremos." testid="f-trajectory_expertise"><textarea className="mp-input" rows={4} value={form.trajectory_expertise} onChange={(e) => set("trajectory_expertise", e.target.value)} /></Field>
            <Field label="¿Hubo algún momento, decisión o experiencia que haya cambiado significativamente tu manera de entender este tema?" testid="f-trajectory_pivot"><textarea className="mp-input" rows={3} value={form.trajectory_pivot} onChange={(e) => set("trajectory_pivot", e.target.value)} /></Field>
            <Field label="¿Qué experiencia, proyectos o resultados consideras que mejor representan tu trayectoria hasta hoy?" testid="f-trajectory_representative"><textarea className="mp-input" rows={3} value={form.trajectory_representative} onChange={(e) => set("trajectory_representative", e.target.value)} /></Field>
            <Field label="¿Qué haces actualmente y cuál es tu rol, negocio o principal área de especialidad?" testid="f-trajectory_current_role"><textarea className="mp-input" rows={3} value={form.trajectory_current_role} onChange={(e) => set("trajectory_current_role", e.target.value)} /></Field>
            <div data-testid="f-trajectory_business_size">
              <div className="mp-label">Si eres empresario(a) o diriges una organización: ¿qué tamaño u operación tiene actualmente?</div>
              <div className="text-mp-mute text-xs -mt-1 mb-2 leading-relaxed">Por ejemplo: años operando, número de empleados, clientes, mercados, ubicaciones, volumen, crecimiento u otras cifras que ayuden a dimensionarla.</div>
              <textarea className="mp-input" rows={3} value={form.trajectory_business_size} onChange={(e) => set("trajectory_business_size", e.target.value)} />
            </div>
            <Field label="¿Hay alguna opinión, consejo o forma de hacer las cosas en tu área de experiencia con la que sabes que muchas personas no estarían de acuerdo?" testid="f-trajectory_controversial"><textarea className="mp-input" rows={3} value={form.trajectory_controversial} onChange={(e) => set("trajectory_controversial", e.target.value)} /></Field>
          </StepShell>
        )}

        {currentStep === "conversacion" && (
          <StepShell title={<>La <span className="text-mp-orange">conversación</span>.</>} n="03">
            <div>
              <div className="mp-label">¿Cuáles son 3 temas de los que te gustaría hablar en Modo Pro?</div>
              {form.topics.map((t, i) => (
                <input key={i} data-testid={`f-topic-${i}`} className="mp-input mb-3" placeholder={`Tema ${i + 1}`} value={t} onChange={(e) => { const nt = [...form.topics]; nt[i] = e.target.value; set("topics", nt); }} />
              ))}
            </div>
            <Field label="¿Hay alguna historia que casi nunca hayas contado públicamente y que estarías dispuesto(a) a compartir?" testid="f-untold_story"><textarea className="mp-input" rows={3} value={form.untold_story} onChange={(e) => set("untold_story", e.target.value)} /></Field>
            <Field label="¿Hay algo sobre tu industria o profesión que la mayoría de las personas entiende mal?" testid="f-industry_misconception"><textarea className="mp-input" rows={3} value={form.industry_misconception} onChange={(e) => set("industry_misconception", e.target.value)} /></Field>
            <Field label="¿Qué pregunta te gustaría que Elda te hiciera?" testid="f-desired_question"><textarea className="mp-input" rows={3} value={form.desired_question} onChange={(e) => set("desired_question", e.target.value)} /></Field>

            <div data-testid="f-numeric_example">
              <div className="mp-label">En tu tema, danos un ejemplo con números reales.</div>
              <div className="text-mp-mute text-xs -mt-1 mb-2 leading-relaxed italic">Ejemplo: En una hipoteca de $800,000, pagar 1% más de interés puede terminar costándote más de $200,000 extra a lo largo de 30 años.</div>
              <textarea className="mp-input" rows={3} value={form.numeric_example} onChange={(e) => set("numeric_example", e.target.value)} />
            </div>

            <div className="mt-8 border border-mp-orange/40 p-6 bg-mp-orange/5">
              <div className="mp-eyebrow">Importante</div>
              <Field label="¿Hay algún tema que prefieras NO abordar durante la entrevista?" testid="f-topics_to_avoid" className="!mt-4"><textarea className="mp-input" rows={3} value={form.topics_to_avoid} onChange={(e) => set("topics_to_avoid", e.target.value)} /></Field>
            </div>

            <div className="mt-8">
              <div className="mp-label">¿Has participado anteriormente en podcasts, televisión, radio u otras entrevistas?</div>
              <div className="flex gap-4">
                {["Sí", "No"].map((label) => {
                  const val = label === "Sí";
                  const active = form.previous_media === val;
                  return (
                    <button key={label} type="button" data-testid={`f-prev_media-${label}`} onClick={() => set("previous_media", val)} className={`px-6 py-3 border ${active ? "border-mp-orange text-mp-orange" : "border-white/20 text-white/70"} font-display text-xs tracking-[0.24em] uppercase transition-colors duration-300`}>
                      {label}
                    </button>
                  );
                })}
              </div>
              {form.previous_media && (
                <div className="mt-6">
                  <div className="text-mp-mute text-sm mb-3">Compártenos los enlaces que consideres más relevantes.</div>
                  {form.previous_media_urls.map((u, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input data-testid={`f-prev_url-${i}`} className="mp-input" placeholder="https://…" value={u} onChange={(e) => { const nu = [...form.previous_media_urls]; nu[i] = e.target.value; set("previous_media_urls", nu); }} />
                      {form.previous_media_urls.length > 1 && (
                        <button type="button" onClick={() => set("previous_media_urls", form.previous_media_urls.filter((_, j) => j !== i))} className="text-mp-mute hover:text-mp-orange"><X size={16} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => set("previous_media_urls", [...form.previous_media_urls, ""])} className="mt-2 text-mp-orange text-xs tracking-[0.24em] uppercase font-display">+ Agregar enlace</button>
                </div>
              )}
            </div>
          </StepShell>
        )}

        {currentStep === "fotos" && (
          <StepShell title={<>Ayúdanos a contar tu <span className="text-mp-orange">historia</span>.</>} n="04">
            <p className="text-white/70 max-w-2xl mb-10">
              Estas fotografías pueden ayudarnos a crear la introducción audiovisual de tu episodio
              y contenido relacionado con tu participación.
            </p>
            {PHOTO_CATEGORIES.map((cat) => (
              <PhotoCategory key={cat.key} cat={cat} files={uploads[cat.key] || []} onAdd={(files) => setUploads((u) => ({ ...u, [cat.key]: [...(u[cat.key] || []), ...files].slice(0, cat.max || 20) }))} onRemove={(idx) => setUploads((u) => ({ ...u, [cat.key]: (u[cat.key] || []).filter((_, i) => i !== idx) }))} />
            ))}
            <div className="mt-14 border-t border-mp-orange/40 pt-10">
              <div className="mp-eyebrow">Tu negocio o proyecto</div>
              <h3 className="mt-4 font-display font-bold text-white text-3xl md:text-4xl leading-tight tracking-[-0.015em]">
                Sube el logotipo y las <span className="text-mp-orange">fotos de tus productos o servicios</span>.
              </h3>
              <p className="mt-4 text-white/70 max-w-2xl">
                Sube el logotipo de tu empresa, negocio o proyecto y, si aplica, hasta 3 fotos de tus productos o servicios.
              </p>
              <div className="mt-6 space-y-4">
                <PhotoCategory
                  cat={{ key: "business_logo", title: "Logotipo", desc: "Sube el logotipo de tu empresa, negocio o proyecto." }}
                  files={uploads.business_logo || []}
                  onAdd={(files) => setUploads((u) => ({ ...u, business_logo: files.slice(0, 1) }))}
                  onRemove={() => setUploads((u) => ({ ...u, business_logo: [] }))}
                  single
                />
                <PhotoCategory
                  cat={{ key: "business_products", title: "Productos o servicios", desc: "Opcional. Hasta 3 fotos de tus productos o servicios.", max: 3 }}
                  files={uploads.business_products || []}
                  onAdd={(files) => setUploads((u) => ({ ...u, business_products: [...(u.business_products || []), ...files].slice(0, 3) }))}
                  onRemove={(idx) => setUploads((u) => ({ ...u, business_products: (u.business_products || []).filter((_, i) => i !== idx) }))}
                />
              </div>
            </div>
          </StepShell>
        )}

        {currentStep === "produccion" && (
          <StepShell title={<>Notas para <span className="text-mp-orange">producción</span>.</>} n="05">
            <div>
              <div className="mp-label">¿Cómo debemos pronunciar correctamente tu nombre?</div>
              <input data-testid="f-pronunciation_text" className="mp-input" placeholder="Escribe la pronunciación fonética" value={form.pronunciation_text} onChange={(e) => set("pronunciation_text", e.target.value)} />
              <div className="mt-4 flex items-center gap-4">
                <PronunciationRecorder recording={recording} setRecording={setRecording} audioBlob={audioBlob} setAudioBlob={setAudioBlob} />
              </div>
            </div>
            <Field label="¿Hay alguna empresa, proyecto, libro, producto o iniciativa que quieras que conozcamos antes de la entrevista?" testid="f-projects_to_know" className="mt-8"><textarea className="mp-input" rows={4} value={form.projects_to_know} onChange={(e) => set("projects_to_know", e.target.value)} /></Field>
            <div className="mt-8">
              <div className="mp-label">Links adicionales que deberíamos revisar</div>
              {form.additional_links.map((u, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input data-testid={`f-add_link-${i}`} className="mp-input" placeholder="https://…" value={u} onChange={(e) => { const nu = [...form.additional_links]; nu[i] = e.target.value; set("additional_links", nu); }} />
                  {form.additional_links.length > 1 && (
                    <button type="button" onClick={() => set("additional_links", form.additional_links.filter((_, j) => j !== i))} className="text-mp-mute hover:text-mp-orange"><X size={16} /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => set("additional_links", [...form.additional_links, ""])} className="mt-2 text-mp-orange text-xs tracking-[0.24em] uppercase font-display">+ Agregar enlace</button>
            </div>
            <Field label="¿Hay alguna información adicional que nuestro equipo de producción debería conocer?" testid="f-additional_info" className="mt-8"><textarea className="mp-input" rows={4} value={form.additional_info} onChange={(e) => set("additional_info", e.target.value)} /></Field>

            <div className="mt-12 border border-white/10 p-6 bg-mp-off">
              <label className="flex items-start gap-4 cursor-pointer">
                <input data-testid="f-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 accent-mp-orange w-4 h-4" />
                <span className="text-white/80 text-sm leading-relaxed">
                  He leído y acepto que los materiales que voluntariamente he proporcionado podrán
                  ser utilizados por Modo Pro en conexión con la producción de mi episodio,
                  incluyendo introducción, miniaturas, clips para redes sociales y material
                  promocional relacionado con mi participación. No transfiero la propiedad de
                  ningún derecho de autor.
                </span>
              </label>
            </div>
          </StepShell>
        )}

        {/* Nav */}
        <div className="mt-14 flex items-center justify-between border-t border-white/10 pt-8">
          <button
            data-testid="invitado-back"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className={`inline-flex items-center gap-2 font-display text-xs tracking-[0.24em] uppercase ${step === 0 ? "text-mp-dim" : "text-white/80 hover:text-white"}`}
          >
            <ArrowLeft size={14} /> Anterior
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="invitado-next" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} className="mp-btn-primary">
              Siguiente <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="invitado-submit" disabled={submitting || !form.consent} onClick={submit} className="mp-btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
              {submitting ? "Enviando…" : <>Enviar mi información <ArrowRight size={16} /></>}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

const StepShell = ({ n, title, children }) => (
  <div>
    <div className="mp-eyebrow">Paso {n}</div>
    <h2 className="mt-4 font-display font-bold text-white text-3xl md:text-5xl leading-[1] tracking-[-0.02em]">{title}</h2>
    <div className="mt-10 space-y-8">{children}</div>
  </div>
);

const Grid2 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">{children}</div>;

const Field = ({ label, testid, children, span, className = "" }) => (
  <div data-testid={testid} className={`${span === 2 ? "md:col-span-2" : ""} ${className}`}>
    <div className="mp-label">{label}</div>
    {children}
  </div>
);

function PhotoCategory({ cat, files, onAdd, onRemove, single }) {
  const handle = (e) => {
    const list = Array.from(e.target.files || []).map((f) => ({ _file: f, name: f.name, size: f.size, url: URL.createObjectURL(f) }));
    onAdd(list);
    e.target.value = "";
  };
  return (
    <div data-testid={`upload-${cat.key}`} className="border border-white/10 p-6 mb-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-white text-lg tracking-[-0.01em]">{cat.title}</div>
          <div className="text-mp-mute text-sm mt-1">{cat.desc}</div>
        </div>
        <label className="mp-btn-ghost cursor-pointer !py-2 !px-4">
          <Upload size={14} /> {single ? "Elegir" : "Agregar"}
          <input type="file" accept="image/*,.heic,.heif" multiple={!single} onChange={handle} className="hidden" />
        </label>
      </div>
      {files.length > 0 && (
        <div className="mt-5 grid grid-cols-3 md:grid-cols-5 gap-3">
          {files.map((f, i) => (
            <div key={i} className="relative aspect-square border border-white/10 overflow-hidden group">
              <img src={f.url} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => onRemove(i)} className="absolute top-1 right-1 bg-black/80 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PronunciationRecorder({ recording, setRecording, audioBlob, setAudioBlob }) {
  const [mediaRec, setMediaRec] = useState(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      setMediaRec(rec);
      setRecording(true);
    } catch (e) {
      toast.error("No se pudo acceder al micrófono");
    }
  };
  const stop = () => { mediaRec?.stop(); setRecording(false); };
  const clear = () => setAudioBlob(null);

  return (
    <div data-testid="pronunciation-recorder" className="flex items-center gap-3">
      {!recording && !audioBlob && (
        <button type="button" onClick={start} className="mp-btn-ghost !py-2 !px-4"><Mic size={14} /> Grabar pronunciación</button>
      )}
      {recording && (
        <button type="button" onClick={stop} className="mp-btn-primary !py-2 !px-4"><StopCircle size={14} /> Detener</button>
      )}
      {audioBlob && (
        <>
          <audio controls src={URL.createObjectURL(audioBlob)} className="h-8" />
          <button type="button" onClick={clear} className="text-mp-mute hover:text-mp-orange"><X size={14} /></button>
          <Check size={14} className="text-mp-orange" />
        </>
      )}
    </div>
  );
}
